import { memo } from "react";
import { Citizen } from "@/types";
import { formatDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MoreHorizontal, Edit } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TableCell, TableRow } from "@/components/ui/table";

interface CitizenTableRowProps {
  citizen: Citizen;
  onEdit: (id: number) => void;
}

interface CitizenTag {
  tag_id: number;
  tag_label: string;
}

export const CitizenTableRow = memo(function CitizenTableRow({
  citizen,
  onEdit,
}: CitizenTableRowProps) {
  const DEFAULT_IMAGE = "/images/default-user-profile-pic.png";

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 overflow-hidden rounded-full bg-muted">
            {citizen.main_pic ? (
              <img
                src={citizen.main_pic}
                alt={citizen.citizen_name}
                className="h-full w-full object-cover"
              />
            ) : (
              <img
                src={DEFAULT_IMAGE}
                alt={citizen.citizen_name}
                className="h-full w-full object-cover"
              />
            )}
          </div>
          <div>
            <p className="font-medium">{citizen.citizen_name}</p>
            <p className="text-xs text-muted-foreground">
              <a
                className="text-blue-500 hover:text-blue-600"
                href={`${import.meta.env.VITE_FRONTEND_URL}/profile/${
                  citizen.slug
                }`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {citizen.slug}
              </a>
            </p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        {citizen.score !== null ? citizen.score.toFixed(2) : "N/A"}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {citizen.tags && citizen.tags.length > 0 ? (
            citizen.tags.map((tag) => (
              <Badge
                key={tag.tag_id}
                variant="outline"
                className="bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100 hover:text-yellow-800"
              >
                {tag.tag_label}
              </Badge>
            ))
          ) : (
            <span className="text-muted-foreground text-sm">No tags</span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Badge
          variant="outline"
          className={
            citizen.is_verified
              ? "border-green-200 bg-green-50 text-green-700"
              : "border-yellow-200 bg-yellow-50 text-yellow-700"
          }
        >
          {citizen.is_verified ? "Verified" : "Unverified"}
        </Badge>
      </TableCell>
      <TableCell>
        {citizen.is_active ? (
          <Badge
            variant="outline"
            className="border-green-200 bg-green-50 text-green-700"
          >
            Active
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="border-red-200 bg-red-50 text-red-700"
          >
            Inactive
          </Badge>
        )}
      </TableCell>
      <TableCell>
        {citizen.modified_date
          ? formatDate(citizen.modified_date)
          : formatDate(citizen.created_date)}
      </TableCell>
      <TableCell className="text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(citizen.citizen_id)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
});
